/**
 * PaymentSlipsCard — Payment slip management in the SO detail sidebar.
 *
 * Design follows OrderDetailSidebar patterns: FieldLabel/FieldValue typography,
 * p-5 sections, mx-5 dividers, subtle status bar, bank logos.
 * White-labeled (no third-party branding).
 *
 * Only renders for storefront orders with bank transfer payment.
 */

import { useRef, useState } from "react";
import {
  CheckCircle,
  Clock,
  FileImage,
  Loader2,
  ShieldCheck,
  Upload,
  XCircle,
  ZoomIn,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { CARD } from "@/config/design-tokens";
import { useLanguage } from "@/i18n/useLanguage";
import { getUITranslation } from "@/lib/views/labels/translations";
import { useAuthStore } from "@/lib/store/authStore";
import {
  usePaymentSlipsByOrder,
  useApprovePayment,
  useRejectSlip,
  useStaffUploadSlip,
  useVerifySlip,
  useVerifySlipEasyslip,
  type PaymentSlip,
} from "@/features/storefront/payment-slips/hooks";
import { useStorefrontTenantSettings } from "@/features/storefront/settings/useStorefrontTenantSettings";

// ---------------------------------------------------------------------------
// Design system tokens — matching OrderDetailSidebar
// ---------------------------------------------------------------------------

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
      {children}
    </div>
  );
}

function FieldValue({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 break-words leading-relaxed">
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bank logo mapping — major Thai banks
// ---------------------------------------------------------------------------

const BANK_LOGO: Record<string, string> = {
  Kasikornbank: "/banks/KBANK.png",
  KBank: "/banks/KBANK.png",
  KBANK: "/banks/KBANK.png",
  "Bangkok Bank": "/banks/BBL.png",
  BBL: "/banks/BBL.png",
  SCB: "/banks/SCB.png",
  "Siam Commercial Bank": "/banks/SCB.png",
  "Krungthai Bank": "/banks/KTB.png",
  KTB: "/banks/KTB.png",
  Krungsri: "/banks/BAY.png",
  "Bank of Ayudhya": "/banks/BAY.png",
  BAY: "/banks/BAY.png",
  TTB: "/banks/TTB.png",
  TMBThanachart: "/banks/TTB.png",
  GSB: "/banks/GSB.png",
  "Government Savings Bank": "/banks/GSB.png",
};

// ---------------------------------------------------------------------------
// Status bar config — subtle tinted bg at top of card
// ---------------------------------------------------------------------------

const STATUS_BAR: Record<string, {
  bg: string; border: string; text: string;
  icon: typeof CheckCircle; label: { en: string; th: string };
}> = {
  Verified: { bg: "bg-green-50 dark:bg-green-950/30", border: "border-b border-green-100 dark:border-green-900", text: "text-green-700 dark:text-green-400", icon: CheckCircle, label: { en: "Verified", th: "ยืนยันแล้ว" } },
  Pending: { bg: "bg-amber-50 dark:bg-amber-950/30", border: "border-b border-amber-100 dark:border-amber-900", text: "text-amber-700 dark:text-amber-400", icon: Clock, label: { en: "Pending Review", th: "รอตรวจสอบ" } },
  Uploaded: { bg: "bg-blue-50 dark:bg-blue-950/30", border: "border-b border-blue-100 dark:border-blue-900", text: "text-blue-700 dark:text-blue-400", icon: Upload, label: { en: "Uploaded", th: "อัปโหลดแล้ว" } },
  Rejected: { bg: "bg-red-50 dark:bg-red-950/30", border: "border-b border-red-100 dark:border-red-900", text: "text-red-700 dark:text-red-400", icon: XCircle, label: { en: "Rejected", th: "ปฏิเสธ" } },
};

// White-labeled verification method badges
const VERIFY_BADGE: Record<string, { label: { en: string; th: string }; className: string }> = {
  "Auto-Verified": {
    label: { en: "Bank Verified", th: "ยืนยันจากธนาคาร" },
    className: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-800",
  },
  Manual: {
    label: { en: "Manual Verify", th: "ยืนยันด้วยตนเอง" },
    className: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-800",
  },
  "Staff Override": {
    label: { en: "Staff Verified", th: "พนักงานยืนยัน" },
    className: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-800",
  },
  Skipped: {
    label: { en: "Skipped", th: "ข้าม" },
    className: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-800",
  },
  Failed: {
    label: { en: "Failed", th: "ยืนยันไม่สำเร็จ" },
    className: "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-400 dark:border-red-800",
  },
  "Quota Exceeded": {
    label: { en: "Quota Exceeded", th: "เกินโควต้า" },
    className: "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/30 dark:text-orange-400 dark:border-orange-800",
  },
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Subtle tinted status bar — flush top of card */
function SubtleBar({ status, easyslipStatus, language }: {
  status: string;
  easyslipStatus?: string;
  language: "en" | "th";
}) {
  const cfg = STATUS_BAR[status] ?? STATUS_BAR.Pending;
  const Icon = cfg.icon;
  const verify = easyslipStatus ? VERIFY_BADGE[easyslipStatus] : undefined;
  return (
    <div className={cn(cfg.bg, cfg.border, "px-5 py-2.5 flex items-center justify-between")}>
      <div className={cn("flex items-center gap-1.5", cfg.text)}>
        <Icon className="h-3.5 w-3.5" />
        <span className="text-xs font-semibold">{cfg.label[language]}</span>
      </div>
      {verify && (
        <Badge variant="outline" className={cn("text-[10px] px-2 py-0.5", verify.className)}>
          <ShieldCheck className="mr-1 h-3 w-3" />{verify.label[language]}
        </Badge>
      )}
    </div>
  );
}

/** Bank field — logo above text */
function BankField({ label, bank, name }: { label: string; bank: string; name?: string }) {
  const logoSrc = BANK_LOGO[bank];
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      {logoSrc && (
        <img
          src={logoSrc}
          alt={bank}
          className="h-10 w-10 rounded-full object-contain mt-1.5 mb-2"
        />
      )}
      <FieldValue>{bank}</FieldValue>
      {name && (
        <div className="text-xs text-muted-foreground mt-1">{name}</div>
      )}
    </div>
  );
}

/** Strip internal plan references like "(Plan 144)" from user-facing text */
function cleanNote(text: string): string {
  return text.replace(/\s*\(Plan\s+\d+\)/gi, "").trim();
}

// ---------------------------------------------------------------------------
// SlipContent — single slip rendered with design-system sections
// ---------------------------------------------------------------------------

function SlipContent({
  slip,
  language,
  tenantServerUrl,
  customerName,
}: {
  slip: PaymentSlip;
  language: "en" | "th";
  tenantServerUrl: string;
  customerName?: string;
}) {
  const [zoomOpen, setZoomOpen] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const verifyManualMutation = useVerifySlip();
  const verifyEasyslipMutation = useVerifySlipEasyslip();
  const approveMutation = useApprovePayment();
  const rejectMutation = useRejectSlip();
  const t = (en: string, th: string) => (language === "th" ? th : en);

  const hasAmount = slip.verified_amount != null && slip.verified_amount > 0;
  const dateStr = slip.uploaded_at || slip.creation;
  const isPending = slip.status === "Pending" || slip.status === "Uploaded";
  const isRejected = slip.status === "Rejected";
  const showActions = isPending || isRejected;
  const hasBank = slip.sender_bank || slip.receiver_bank;

  const imgUrl = slip.slip_image?.startsWith("/")
    ? `${tenantServerUrl}${slip.slip_image}`
    : slip.slip_image;

  async function handleVerifyManually() {
    try {
      if (slip.status === "Uploaded" || slip.status === "Rejected") {
        const result = await approveMutation.mutateAsync(slip.name);
        if (result.payment_entry_warning) {
          toast.warning(result.payment_entry_warning);
        } else {
          toast.success(t("Slip verified manually", "ยืนยันสลิปด้วยตนเองเรียบร้อย"));
        }
      } else {
        await verifyManualMutation.mutateAsync(slip.name);
        toast.success(t("Slip verified manually", "ยืนยันสลิปด้วยตนเองเรียบร้อย"));
      }
    } catch (err: any) {
      toast.error(err?.message || "Verify failed — please retry");
    }
  }

  async function handleVerifyEasyslip() {
    try {
      const result = await verifyEasyslipMutation.mutateAsync(slip.name);
      if (result.verified) {
        if (result.payment_entry_warning) {
          toast.warning(result.payment_entry_warning);
        } else {
          toast.success(t("Slip verified via bank", "ยืนยันสลิปจากธนาคารเรียบร้อย"));
        }
      } else {
        toast.warning(
          result.verification_note || t("Verification inconclusive — verify manually", "ยืนยันไม่สมบูรณ์ — กรุณายืนยันด้วยตนเอง"),
        );
      }
    } catch (err: any) {
      toast.error(err?.message || "EasySlip verify failed — please retry");
    }
  }

  async function handleReject() {
    try {
      await rejectMutation.mutateAsync({ slipName: slip.name, reason: rejectReason });
      toast.success(t("Slip rejected", "ปฏิเสธสลิปเรียบร้อย"));
      setRejectMode(false);
      setRejectReason("");
    } catch (err: any) {
      toast.error(err?.message || "Reject failed — please retry");
    }
  }

  return (
    <>
      {/* Amount + Date */}
      <div className="p-5">
        <div className="flex flex-col gap-y-5">
          {hasAmount && (
            <div>
              <FieldLabel>{t("Amount", "จำนวนเงิน")}</FieldLabel>
              <div className="text-xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                ฿{slip.verified_amount!.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
            </div>
          )}
          {dateStr && (
            <div>
              <FieldLabel>{t("Uploaded", "อัปโหลดเมื่อ")}</FieldLabel>
              <FieldValue>
                {new Date(dateStr).toLocaleString(
                  language === "th" ? "th-TH" : "en-US",
                  { dateStyle: "medium", timeStyle: "short" },
                )}
              </FieldValue>
            </div>
          )}
        </div>
      </div>

      {/* Slip image */}
      {imgUrl && (
        <>
          <div className="mx-5 border-t border-border/40" />
          <div className="p-5">
            <button
              type="button"
              onClick={() => setZoomOpen(true)}
              className="group relative w-full overflow-hidden rounded-lg"
            >
              <img
                src={imgUrl}
                alt="Payment slip"
                className="w-full object-contain max-h-52"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/10">
                <ZoomIn className="h-5 w-5 text-white opacity-0 drop-shadow-md transition-opacity group-hover:opacity-100" />
              </div>
            </button>
          </div>
        </>
      )}

      {/* Bank details */}
      {hasBank && (
        <>
          <div className="mx-5 border-t border-border/40" />
          <div className="p-5">
            <div className="grid grid-cols-2 gap-x-6 gap-y-5">
              {slip.sender_bank && (
                <BankField
                  label={t("From", "จาก")}
                  bank={slip.sender_bank}
                  name={slip.sender_name}
                />
              )}
              {slip.receiver_bank && (
                <BankField
                  label={t("To", "ถึง")}
                  bank={slip.receiver_bank}
                  name={slip.receiver_name}
                />
              )}
            </div>
          </div>
        </>
      )}

      {/* Reference & meta */}
      <div className="mx-5 border-t border-border/40" />
      <div className="p-5">
        <div className="grid grid-cols-2 gap-x-6 gap-y-5">
          <div>
            <FieldLabel>{t("Slip ID", "รหัสสลิป")}</FieldLabel>
            <div className="text-sm font-mono text-muted-foreground">{slip.name}</div>
          </div>
          {slip.trans_ref && (
            <div>
              <FieldLabel>{t("Trans Ref", "อ้างอิง")}</FieldLabel>
              <div className="text-sm font-mono text-muted-foreground">{slip.trans_ref}</div>
            </div>
          )}
          <div>
            <FieldLabel>{t("Uploaded By", "อัปโหลดโดย")}</FieldLabel>
            <FieldValue>
              {slip.owner?.startsWith("line_") && customerName
                ? customerName
                : slip.owner ?? t("Unknown", "ไม่ทราบ")}
            </FieldValue>
            <div className="text-[10px] text-muted-foreground mt-0.5">
              {slip.owner?.startsWith("line_") ? t("Customer", "ลูกค้า") : t("Staff", "พนักงาน")}
            </div>
          </div>
          {slip.easyslip_status && (
            <div>
              <FieldLabel>{t("Verify Method", "วิธียืนยัน")}</FieldLabel>
              {(() => {
                const verify = VERIFY_BADGE[slip.easyslip_status];
                return verify ? (
                  <Badge variant="outline" className={cn("text-[10px] px-2 py-0.5 mt-1", verify.className)}>
                    <ShieldCheck className="mr-1 h-3 w-3" />{verify.label[language]}
                  </Badge>
                ) : (
                  <FieldValue>{slip.easyslip_status}</FieldValue>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Approval trail */}
      {slip.status === "Verified" && slip.verified_by && (
        <>
          <div className="mx-5 border-t border-border/40" />
          <div className="p-5">
            <div className="flex items-start gap-2.5">
              <CheckCircle className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <FieldLabel>{t("Verified By", "ยืนยันโดย")}</FieldLabel>
                <FieldValue>{slip.verified_by}</FieldValue>
                {slip.verified_at && (
                  <div className="text-xs text-muted-foreground mt-1">
                    {new Date(slip.verified_at).toLocaleString(
                      language === "th" ? "th-TH" : "en-US",
                      { dateStyle: "medium", timeStyle: "short" },
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Rejection trail */}
      {slip.status === "Rejected" && (
        <>
          <div className="mx-5 border-t border-border/40" />
          <div className="p-5">
            <div className="flex items-start gap-2.5">
              <XCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <FieldLabel>{t("Rejection Reason", "เหตุผลที่ปฏิเสธ")}</FieldLabel>
                <div className="text-sm font-medium text-red-600 dark:text-red-400">
                  {slip.rejection_reason || t("Rejected", "ปฏิเสธ")}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Note */}
      {slip.verification_note && (() => {
        const note = cleanNote(slip.verification_note);
        if (!note) return null;
        return (
          <>
            <div className="mx-5 border-t border-border/40" />
            <div className="p-5">
              <FieldLabel>{t("Note", "หมายเหตุ")}</FieldLabel>
              <div className="text-sm text-muted-foreground italic">{note}</div>
            </div>
          </>
        );
      })()}

      {/* Actions — 3 buttons for all unverified slips */}
      {showActions && (
        <>
          <div className="mx-5 border-t border-border/40" />
          <div className="p-5">
            {!rejectMode ? (
              <div className="flex flex-col gap-2.5">
                <div className="flex items-center gap-2.5">
                  <button
                    onClick={handleVerifyManually}
                    disabled={verifyManualMutation.isPending || verifyEasyslipMutation.isPending || approveMutation.isPending}
                    className="flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
                  >
                    {(verifyManualMutation.isPending || approveMutation.isPending) ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <CheckCircle className="h-4 w-4" />
                    )}
                    <span className="text-[13px] font-semibold">{t("Verify Manually", "ยืนยันด้วยตนเอง")}</span>
                  </button>
                  <button
                    onClick={handleVerifyEasyslip}
                    disabled={verifyManualMutation.isPending || verifyEasyslipMutation.isPending || approveMutation.isPending}
                    className="flex items-center gap-2 rounded-xl border border-border bg-card px-3.5 py-2.5 transition-colors hover:bg-emerald-50 dark:hover:bg-emerald-950/30 disabled:opacity-50"
                  >
                    {verifyEasyslipMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ShieldCheck className="h-4 w-4 text-emerald-600" />
                    )}
                    <span className="text-[13px] font-medium text-emerald-700 dark:text-emerald-400">{t("Verify with EasySlip", "ยืนยันด้วย EasySlip")}</span>
                  </button>
                </div>
                {!isRejected && (
                  <button
                    onClick={() => setRejectMode(true)}
                    disabled={rejectMutation.isPending}
                    className="flex items-center gap-2 rounded-xl border border-border bg-card px-3.5 py-2.5 transition-colors hover:bg-red-50 dark:hover:bg-red-950/30 self-start"
                  >
                    <XCircle className="h-4 w-4 text-red-500" />
                    <span className="text-[13px] font-medium text-red-600 dark:text-red-400">{t("Reject", "ปฏิเสธ")}</span>
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <Input
                  placeholder={t("Reason (optional)", "เหตุผล (ไม่บังคับ)")}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  autoFocus
                />
                <div className="flex items-center gap-2.5">
                  <button
                    onClick={handleReject}
                    disabled={rejectMutation.isPending}
                    className="flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-white transition-colors hover:bg-red-700 disabled:opacity-50"
                  >
                    {rejectMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                    <span className="text-[13px] font-semibold">{t("Confirm Reject", "ยืนยันปฏิเสธ")}</span>
                  </button>
                  <button
                    onClick={() => { setRejectMode(false); setRejectReason(""); }}
                    className="flex items-center gap-2 rounded-xl border border-border bg-card px-3.5 py-2.5 transition-colors hover:bg-muted"
                  >
                    <span className="text-[13px] font-medium">{t("Cancel", "ยกเลิก")}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Zoom dialog */}
      <Dialog open={zoomOpen} onOpenChange={setZoomOpen}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden">
          {imgUrl && (
            <img
              src={imgUrl}
              alt="Payment slip (full size)"
              className="w-full object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Upload dropzone
// ---------------------------------------------------------------------------

function UploadDropzone({
  salesOrderName,
  language,
}: {
  salesOrderName: string;
  language: "en" | "th";
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const uploadMutation = useStaffUploadSlip();
  const t = (en: string, th: string) => (language === "th" ? th : en);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    try {
      const result = await uploadMutation.mutateAsync({
        salesOrder: salesOrderName,
        file: selectedFile,
        note: note || undefined,
      });
      if (result.payment_entry_warning) {
        toast.warning(result.payment_entry_warning);
      } else {
        toast.success(t("Slip uploaded", "อัปโหลดสลิปเรียบร้อย"));
      }
      setSelectedFile(null);
      setPreview(null);
      setNote("");
    } catch (err: any) {
      toast.error(err?.message || "Upload failed — please retry");
    }
  };

  const handleClear = () => {
    setSelectedFile(null);
    setPreview(null);
    setNote("");
  };

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        onChange={handleFileChange}
        className="hidden"
      />

      {preview ? (
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="w-full overflow-hidden rounded-lg border border-border"
          >
            <img
              src={preview}
              alt="Slip preview"
              className="w-full object-contain max-h-48"
            />
          </button>
          <Input
            placeholder={t("Note (optional)", "หมายเหตุ (ไม่บังคับ)")}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="flex items-center gap-2.5">
            <button
              onClick={handleUpload}
              disabled={uploadMutation.isPending}
              className="flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
            >
              {uploadMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              <span className="text-[13px] font-semibold">{t("Upload Slip", "อัปโหลดสลิป")}</span>
            </button>
            <button
              onClick={handleClear}
              className="flex items-center gap-2 rounded-xl border border-border bg-card px-3.5 py-2.5 transition-colors hover:bg-muted"
            >
              <span className="text-[13px] font-medium">{t("Cancel", "ยกเลิก")}</span>
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="flex w-full items-center justify-center gap-2.5 rounded-xl border-2 border-dashed border-border bg-muted/20 px-4 py-6 text-muted-foreground transition-colors hover:bg-muted/40 hover:border-primary/30"
        >
          <Upload className="h-5 w-5" />
          <span className="text-sm font-medium">{t("Upload payment slip", "อัปโหลดสลิปชำระเงิน")}</span>
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main card
// ---------------------------------------------------------------------------

interface PaymentSlipsCardProps {
  salesOrderName: string;
  paymentMethodType: string;
  isStorefront: boolean;
  orderStatus: string;
  customerName?: string;
}

export function PaymentSlipsCard({
  salesOrderName,
  paymentMethodType,
  isStorefront,
  orderStatus,
  customerName,
}: PaymentSlipsCardProps) {
  const { language } = useLanguage();
  const tenantServerUrl = useAuthStore((s) => s.tenantServerUrl);
  const { settings: tenantSettings } = useStorefrontTenantSettings();

  const isBankTransfer = isStorefront && (paymentMethodType === "Bank Transfer" || paymentMethodType === "Event Checkout");
  const { data: rawSlips, isLoading } = usePaymentSlipsByOrder(
    isBankTransfer ? salesOrderName : undefined,
  );
  const slips: PaymentSlip[] = Array.isArray(rawSlips) ? rawSlips : [];

  if (!isBankTransfer) return null;

  const canStaffUpload =
    orderStatus === "To Deliver and Bill" &&
    !!tenantSettings?.allow_staff_manual_verification;

  if (!isLoading && slips.length === 0 && !canStaffUpload) return null;

  const g = (key: string, fb: string) =>
    getUITranslation(key, language) || fb;

  const isSingle = slips.length === 1;

  return (
    <div className={CARD}>
      {/* Loading */}
      {isLoading ? (
        <>
          <div className="px-5 py-2.5 bg-muted/50 border-b border-border/40">
            <div className="h-3.5 w-20 bg-muted animate-pulse rounded" />
          </div>
          <div className="p-5 space-y-5">
            <div className="space-y-2">
              <div className="h-2.5 w-12 bg-muted animate-pulse rounded" />
              <div className="h-6 w-28 bg-muted animate-pulse rounded" />
            </div>
            <div className="h-44 bg-muted animate-pulse rounded-lg" />
            <div className="grid grid-cols-2 gap-x-6 gap-y-5">
              <div className="space-y-2">
                <div className="h-2.5 w-10 bg-muted animate-pulse rounded" />
                <div className="h-4 w-24 bg-muted animate-pulse rounded" />
              </div>
              <div className="space-y-2">
                <div className="h-2.5 w-10 bg-muted animate-pulse rounded" />
                <div className="h-4 w-20 bg-muted animate-pulse rounded" />
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Single slip — status bar at top, no header needed */}
          {isSingle && (
            <>
              <SubtleBar
                status={slips[0].status}
                easyslipStatus={slips[0].easyslip_status}
                language={language}
              />
              <SlipContent
                slip={slips[0]}
                language={language}
                tenantServerUrl={tenantServerUrl ?? ""}
                customerName={customerName}
              />
            </>
          )}

          {/* Multi-slip — header + stacked slips */}
          {!isSingle && slips.length > 0 && (
            <>
              <div className="p-5">
                <FieldLabel>
                  {g("payment_slips", "Payment Slips")} ({slips.length})
                </FieldLabel>
              </div>

              {slips.map((slip, i) => {
                const cfg = STATUS_BAR[slip.status] ?? STATUS_BAR.Pending;
                const Icon = cfg.icon;
                const verify = slip.easyslip_status ? VERIFY_BADGE[slip.easyslip_status] : undefined;

                return (
                  <div key={slip.name}>
                    <div className={cn("border-t border-border", i === 0 && "border-border/40")} />

                    {/* Inline status header */}
                    <div className="px-5 pt-5 pb-3 flex items-center justify-between">
                      <div className={cn("flex items-center gap-1.5", cfg.text)}>
                        <Icon className="h-3.5 w-3.5" />
                        <span className="text-xs font-semibold">{cfg.label[language]}</span>
                      </div>
                      {verify && (
                        <Badge variant="outline" className={cn("text-[10px] px-2 py-0.5", verify.className)}>
                          <ShieldCheck className="mr-1 h-3 w-3" />{verify.label[language]}
                        </Badge>
                      )}
                    </div>

                    <SlipContent
                      slip={slip}
                      language={language}
                      tenantServerUrl={tenantServerUrl ?? ""}
                      customerName={customerName}
                    />
                  </div>
                );
              })}
            </>
          )}

          {/* Upload dropzone */}
          {canStaffUpload && (
            <>
              {slips.length > 0 && <div className="border-t border-border" />}
              <div className="p-5">
                <UploadDropzone
                  salesOrderName={salesOrderName}
                  language={language}
                />
              </div>
            </>
          )}

          {/* Empty state */}
          {slips.length === 0 && !canStaffUpload && (
            <div className="p-5">
              <FieldLabel>{g("payment_slips", "Payment Slips")}</FieldLabel>
              <div className="flex flex-col items-center gap-3 py-8 text-center">
                <FileImage className="h-8 w-8 text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground">
                  {language === "th" ? "ยังไม่มีสลิปชำระเงิน" : "No payment slips yet"}
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
